import { createAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { AnyAction } from "redux";
import type { ThunkDispatch } from "redux-thunk";
import axios from '../../config/axios';
import Toast from "../../shared/toast/Toast";
import { InterviewScheduleTabEnum, InterviewSchedulingInitialState, InterviewSchedulingInterface, InterviewSchedulingFilters, SaveInterviewAppointmentBody, SendInterviewInvitationBody, GetInterviewsScheduleResponse, GetInterviewFiltersResponse, GetInterviewAppointmentsResponse, GetInterviewAppointmentDetailsResponse, GetInterviewAppointmentSlotsResponse, GetInterviewLocationsResponse, ModifyInterviewAppointmentSlotsBody, ModifyInterviewAppointmentLocationBody, InterviewOperationEnum, InterviewInvitationResultEnum, SendInvitationsResponseData, InterviewInvitationStatusEnum, InterviewMessageTemplateTypeEnum } from "src/utils/interfaces/interview-scheduling/InterviewSchedulingTypes";
import { WhatsAppTemplate } from "src/utils/interfaces/whatsapp/WhatsappTemplatesTypes";
import { INTERVIEW_INVITATIONS_PAGE_SIZE } from "src/utils/constants/constants";
import { t } from "i18next";
import { clearApplicantsCacheKeys, invalidateJobPostCache, invalidateJobPostCacheForJobsPreMh, updateObjectInList } from "../../utils/helpers/utils";
import { RootState } from "../../redux/store";
import { forceSetApplicantsList, updateSelectedApplicant, setApplicantsToMoveBetweenKanbanColumns, setKanbanColumnCount, getApplicationStatuses, updateApplicantsScheduledInterviewsInKanbanColumns } from "../vetting/vetting";
import { updateApplicantsWithScheduledInterviews } from "../../utils/helpers/interview-scheduling-utils";
import { updateTemplate } from "../whatsapp/templates/whatsapp-templates";
import { ApplicantListItem } from "src/utils/interfaces/applicants/ApplicantListItem";
import { ApplicantsViewEnum } from "src/utils/interfaces/mass-hiring/MassHiringInterfaces";

const appKey = 'appInterviewScheduling';
const apiBaseUrl = 'pa/ft/interview-invitations';
const apiBaseUrlAppointments = 'pa/ft/interview-appointments';
const apiWhatsappTemplatesUrl = 'pa/whatsapp/templates';



export const getInterviewsSchedule = createAsyncThunk(`${appKey}/getInterviewsSchedule`, async (filters?: InterviewSchedulingFilters) => {
    const finalFilters = { ...filters };
    if (filters?.tab === InterviewScheduleTabEnum.ALL) { finalFilters.tab = undefined; }

    const response = await axios.get(`${apiBaseUrl}`, { params: finalFilters });
    return response.data.data as GetInterviewsScheduleResponse;
});

export const getFilters = createAsyncThunk(`${appKey}/getFilters`, async (filters?: InterviewSchedulingFilters) => {
    const response = await axios.get(`${apiBaseUrl}/filter-options`, { params: filters });
    return response.data.data as GetInterviewFiltersResponse;
});

export const getInterviewAppointments = createAsyncThunk(`${appKey}/getInterviewAppointments`, async () => {
    const response = await axios.get(`${apiBaseUrlAppointments}`,);
    return response.data.data as GetInterviewAppointmentsResponse;
});

export const getInterviewAppointmentDetails = createAsyncThunk(`${appKey}/getInterviewAppointmentDetails`, async (appointmentId: string) => {
    const response = await axios.get(`${apiBaseUrlAppointments}/${appointmentId}`,);
    return response.data.data as GetInterviewAppointmentDetailsResponse;
});

export const getInterviewAppointmentSlots = createAsyncThunk(`${appKey}/getInterviewAppointmentSlots`, async (appointmentId: string) => {
    const response = await axios.get(`${apiBaseUrlAppointments}/${appointmentId}/available-slots`,);
    return response.data.data as GetInterviewAppointmentSlotsResponse;
});

export const saveInterviewAppointment = createAsyncThunk(`${appKey}/saveInterviewAppointment`, async (body: SaveInterviewAppointmentBody) => {
    const response = await axios.post(`${apiBaseUrlAppointments}`, body);
    return response.data.data;
});

export const modifyInterviewAppointmentLocation = createAsyncThunk(`${appKey}/modifyInterviewAppointmentLocation`, async (body: ModifyInterviewAppointmentLocationBody) => {
    const response = await axios.patch(`${apiBaseUrlAppointments}/${body.appointmentId}/location`, body);
    return response.data?.data;
});

export const modifyInterviewAppointmentSlots = createAsyncThunk(`${appKey}/modifyInterviewAppointmentSlots`, async (body: ModifyInterviewAppointmentSlotsBody) => {
    const response = await axios.patch(`${apiBaseUrlAppointments}/${body.appointmentId}/slots`, body);
    return response.data?.data;
});

export const cancelInterviewAppointment = createAsyncThunk(`${appKey}/cancelInterviewAppointment`, async (data: { appointmentId: string; cancellationReason?: string }) => {
    const response = await axios.delete(`${apiBaseUrlAppointments}/${data.appointmentId}`, { data: { cancellationReason: data.cancellationReason } });
    return response.data?.data;
});


/**
 * Updates applicant stage to HR Interview after scheduling interviews
 * This is independent of kanban view and works on any page
 */
const updateApplicantStageAfterSchedulingInterview = async (
    invitedApplicantIds: string[],
    updatedApplicantsList: any[],
    jobId: string | null,
    selectedApplicant: any,
    dispatch: any,
    getState: () => RootState
) => {
    if (invitedApplicantIds.length === 0) {
        return;
    }

    const state = getState();

    // Get application statuses to find HR Interview status
    let applicationStatuses = state.vetting?.applicationStatuses || [];

    // Fetch application statuses if not loaded
    if (applicationStatuses.length === 0 && jobId) {
        try {
            const statusesResult = await dispatch(getApplicationStatuses(jobId));
            if (statusesResult.meta.requestStatus === 'fulfilled' && statusesResult.payload) {
                applicationStatuses = statusesResult.payload as any[];
            }
        } catch (error) {
            console.error('Failed to fetch application statuses:', error);
        }
    }

    // Find HR Interview application status by matching label
    const hrInterviewTitle = 'HR Interview'; // Hardcoded English
    const arabicHrInterview = 'المقابلة الشخصية';

    const hrInterviewStatus = applicationStatuses.find(status => {
        const statusLabel = status.label?.toLowerCase() || '';
        const statusLabelOriginal = status.label || '';

        // Match with hardcoded English
        if (statusLabel === hrInterviewTitle.toLowerCase()) {
            return true;
        }
        // Match with Arabic translation
        if (statusLabelOriginal === arabicHrInterview || statusLabelOriginal.includes(arabicHrInterview)) {
            return true;
        }
        // Partial match for English
        if (statusLabel.includes(hrInterviewTitle)) {
            return true;
        }

        return false;
    });

    // Update applicants list with HR Interview stage information
    if (hrInterviewStatus) {
        // Start with the updated applicants list (which already has scheduled interviews updated)
        let listToUpdate = [...updatedApplicantsList];

        // Update each invited applicant's stage fields and status
        invitedApplicantIds.forEach(applicantId => {
            const updateData: Partial<any> = {
                stageExternalId: hrInterviewStatus.id,
                stageName: hrInterviewStatus.label,
            };

            // Also update the status enum if available
            if (hrInterviewStatus.enum) {
                updateData.status = hrInterviewStatus.enum;
            }

            listToUpdate = updateObjectInList(listToUpdate, applicantId, updateData);
        });

        // Update the applicants list in state
        dispatch(forceSetApplicantsList(listToUpdate));

        // Update selectedApplicant if it's one of the invited applicants
        if (selectedApplicant?.externalId && invitedApplicantIds.includes(selectedApplicant.externalId)) {
            const updatedSelectedApplicant = listToUpdate.find(
                app => app.externalId === selectedApplicant.externalId
            );
            if (updatedSelectedApplicant) {
                dispatch(updateSelectedApplicant(updatedSelectedApplicant));
            }
        }
    }
};

/**
 * Moves applicants to HR Interview column in kanban view after scheduling interviews
 * This only runs if kanban columns exist
 */
const moveApplicantsToHrInterviewColumn = async (
    invitedApplicantIds: string[],
    dispatch: any,
    getState: () => RootState
) => {
    if (invitedApplicantIds.length === 0) {
        return;
    }

    const state = getState();
    const kanbanColumns = state.vetting?.kanbanColumns || [];
    const kanbanColumnsData = state.vetting?.kanbanColumnsData || {};

    // Find HR Interview column by matching title (case-insensitive)
    // Check for both English "HR Interview" and Arabic "المقابلة الشخصية"
    const hrInterviewColumn = kanbanColumns.find(column => {
        const columnTitle = column.title?.toLowerCase() || '';
        const hrInterviewTitle = 'HR Interview'; // Hardcoded English
        const arabicHrInterview = 'المقابلة الشخصية';

        return columnTitle === hrInterviewTitle.toLowerCase() ||
            columnTitle === arabicHrInterview ||
            column.title === arabicHrInterview || // Direct match for Arabic (no lowercase)
            columnTitle.includes(hrInterviewTitle) ||
            column.title?.includes(arabicHrInterview); // Check for Arabic text
    });

    if (!hrInterviewColumn) {
        return;
    }

    // Filter out applicants that are already in the HR Interview column
    const applicantsToMove = invitedApplicantIds.filter(applicantId => {
        const hrInterviewColumnData = kanbanColumnsData[hrInterviewColumn.id];
        const isAlreadyInHrInterview = hrInterviewColumnData?.applicants?.some(
            applicant => applicant.externalId === applicantId
        );
        return !isAlreadyInHrInterview;
    });

    if (applicantsToMove.length === 0) {
        return;
    }

    // Track which applicants come from which source columns for count updates
    const applicantsBySourceColumn = new Map<string, string[]>();
    const kanbanColumnsCounts = state.vetting?.kanbanColumnsCounts || {};

    // Find source column for each applicant
    applicantsToMove.forEach(applicantId => {
        // Find which column currently contains this applicant
        let foundSourceColumnId: string | null = null;

        for (const [columnId, columnData] of Object.entries(kanbanColumnsData)) {
            if (columnId === hrInterviewColumn.id) continue; // Skip destination column

            const hasApplicant = (columnData as { applicants?: Array<{ externalId: string }> })?.applicants?.some(
                applicant => applicant.externalId === applicantId
            );
            if (hasApplicant) {
                foundSourceColumnId = columnId;
                break;
            }
        }

        // If applicant not found in any column, try to find their current stage from applicantsList
        if (!foundSourceColumnId) {
            const currentState = getState();
            const currentApplicantsList = currentState.vetting?.applicantsList || [];
            const applicant = currentApplicantsList.find(
                app => app.externalId === applicantId
            );
            if (applicant?.stageExternalId && applicant.stageExternalId !== hrInterviewColumn.id) {
                foundSourceColumnId = applicant.stageExternalId;
            }
        }

        if (foundSourceColumnId) {
            if (!applicantsBySourceColumn.has(foundSourceColumnId)) {
                applicantsBySourceColumn.set(foundSourceColumnId, []);
            }
            applicantsBySourceColumn.get(foundSourceColumnId)!.push(applicantId);
        }
    });

    // Decrement counts for source columns
    applicantsBySourceColumn.forEach((applicantIds, sourceColumnId) => {
        const sourceColumnData = kanbanColumnsData[sourceColumnId];
        const currentCount = kanbanColumnsCounts[sourceColumnId] ??
            sourceColumnData?.count ??
            0;
        const newCount = Math.max(0, currentCount - applicantIds.length);
        dispatch(setKanbanColumnCount({ columnId: sourceColumnId, count: newCount }));
    });

    // Increment count for HR Interview column
    const hrInterviewColumnData = kanbanColumnsData[hrInterviewColumn.id];
    const currentHrCount = kanbanColumnsCounts[hrInterviewColumn.id] ??
        hrInterviewColumnData?.count ??
        0;
    const newHrCount = currentHrCount + applicantsToMove.length;
    dispatch(setKanbanColumnCount({ columnId: hrInterviewColumn.id, count: newHrCount }));

    // Move applicants to HR Interview column
    dispatch(setApplicantsToMoveBetweenKanbanColumns({
        sourceColumnId: null, // null means search all columns
        applicantIds: applicantsToMove,
        destinationColumnId: hrInterviewColumn.id,
    }));
};


export const sendInterviewInvitation = createAsyncThunk(`${appKey}/sendInterviewInvitation`, async (body: SendInterviewInvitationBody, { getState, dispatch }) => {
    // Validate required fields
    if (!body?.appointmentId) {
        throw new Error('Appointment ID is required');
    }

    const response = await axios.post(`${apiBaseUrlAppointments}/${body.appointmentId}/send-invitations`, body);
    const responseData: SendInvitationsResponseData | undefined = response.data?.data;

    // If response data is missing, still proceed but log a warning
    if (!responseData) {
        console.warn('sendInterviewInvitation: Response data is missing');
    }

    const state = getState() as RootState;
    const jobId = state.vetting?.jobId;
    const applicantsList = state.vetting?.applicantsList || [];
    const selectedApplicant = state.vetting?.selectedApplicant;

    // Clear applicants cache (wrap in try-catch to handle localStorage errors)
    if (jobId) {
        try {
            clearApplicantsCacheKeys('jobId', jobId);
            invalidateJobPostCache(jobId);
        } catch (error) {
            // Log but don't throw - cache clearing is not critical
            console.error('Failed to clear applicants cache:', error);
        }
    }

    // Update applicants list with new scheduledInterviews using helper function
    // Only update if we have valid response data
    if (responseData) {
        const updatedApplicantsList = updateApplicantsWithScheduledInterviews(responseData, body, applicantsList);

        // Check if applicants were actually updated
        // Since .map() always returns a new array, we need to check if any applicant was modified
        // We check if any applicant's scheduledInterviews array length increased (new interviews added)
        const hasChanges = updatedApplicantsList.some((applicant, index) => {
            const originalApplicant = applicantsList[index];
            if (!originalApplicant) {
                return false;
            }
            const originalCount = originalApplicant.scheduledInterviews?.length || 0;
            const updatedCount = applicant.scheduledInterviews?.length || 0;
            return updatedCount > originalCount;
        });

        if (hasChanges) {
            dispatch(forceSetApplicantsList(updatedApplicantsList));
            dispatch(updateApplicantScheduledInterviewsInKanbanView(updatedApplicantsList));

            // Get all applicant IDs that received invitations (exclude skipped ones)
            const allRequestedIds = [
                ...(body.applicationExternalIds || []),
                ...(body.offlineApplicationExternalIds || [])
            ];
            const skippedIds = new Set(responseData?.skippedApplicationIds || []);
            const invitedApplicantIds = allRequestedIds.filter(id => !skippedIds.has(id));

            // Update selectedApplicant if it exists and was one of the invited applicants
            if (selectedApplicant?.externalId) {
                // Check if selectedApplicant was invited
                if (invitedApplicantIds.includes(selectedApplicant.externalId)) {
                    // Find the updated version of the selected applicant from the updated list
                    const updatedSelectedApplicant = updatedApplicantsList.find(
                        applicant => applicant.externalId === selectedApplicant.externalId
                    );

                    if (updatedSelectedApplicant) {
                        // Check if scheduledInterviews actually changed
                        const originalCount = selectedApplicant.scheduledInterviews?.length || 0;
                        const updatedCount = updatedSelectedApplicant.scheduledInterviews?.length || 0;

                        if (updatedCount > originalCount) {
                            dispatch(updateSelectedApplicant(updatedSelectedApplicant));
                        }
                    }
                }
            }

            try {
                // Update applicants list to HR Interview stage (independent of kanban view)
                await updateApplicantStageAfterSchedulingInterview(
                    invitedApplicantIds,
                    updatedApplicantsList,
                    jobId,
                    selectedApplicant,
                    dispatch,
                    getState
                );
            } catch (error) {
                console.error('Failed to update applicant stage after scheduling interview:', error);
            }

            try {
                // Move applicants to HR Interview column in kanban view if applicable
                await moveApplicantsToHrInterviewColumn(
                    invitedApplicantIds,
                    dispatch,
                    getState
                );
            } catch (error) {
                console.error('Failed to move applicants to HR Interview column:', error);
            }
        }
    }

    return responseData;
});


export const getInterviewLocations = createAsyncThunk(`${appKey}/getInterviewLocations`, async () => {
    const response = await axios.get(`${apiBaseUrlAppointments}/recent-locations/list`,);
    return response.data.data as GetInterviewLocationsResponse;
});


export const bulkUpdateInterviewResults = createAsyncThunk(`${appKey}/bulkUpdateInterviewResults`, async (data: { interviewIds: string[], result: InterviewInvitationResultEnum }, { dispatch }) => {
    switch (data.result) {
        case InterviewInvitationResultEnum.REJECTED:
            return await dispatch(rejectInterviewInvitation({ interviewIds: data.interviewIds })).unwrap();
        case InterviewInvitationResultEnum.PASSED:
        case InterviewInvitationResultEnum.PASSED_AND_ONBOARDING:
            return await dispatch(acceptInterviewInvitation({ interviewIds: data.interviewIds, result: data.result })).unwrap();
        case InterviewInvitationResultEnum.NOT_ATTENDED:
            return await dispatch(markAsNotAttendedInterviewInvitation({ interviewIds: data.interviewIds })).unwrap();
        default:
            throw new Error(`Unsupported result: ${data.result}`);
    }
}
);

export const rejectInterviewInvitation = createAsyncThunk(`${appKey}/rejectInterviewInvitation`, async (data: { interviewIds: string[], reason?: string }) => {
    const response = await axios.post(`${apiBaseUrl}/reject`, { invitationExternalIds: data.interviewIds, rejectionReasonId: data.reason },);
    return response.data?.data;
});

export const cancelInterviewInvitation = createAsyncThunk(`${appKey}/cancelInterviewInvitation`, async (data: { interviewIds: string[], reason?: string }, { getState, dispatch }) => {
    const response = await axios.post(`${apiBaseUrl}/cancel`, { invitationExternalIds: data.interviewIds, cancellationReason: data.reason },);
    const responseData = response.data?.data;

    const state = getState() as RootState;
    const applicantsList = state.vetting?.applicantsList || [];
    const selectedApplicant = state.vetting?.selectedApplicant;

    // Update applicants list to mark cancelled interviews with CANCELLED status
    const updatedApplicantsList = applicantsList.map(applicant => {
        if (!applicant.scheduledInterviews || applicant.scheduledInterviews.length === 0) {
            return applicant;
        }

        // Check if this applicant has any of the cancelled interviews
        const hasCancelledInterviews = applicant.scheduledInterviews.some(
            interview => data.interviewIds.includes(interview.externalId)
        );

        if (!hasCancelledInterviews) {
            return applicant;
        }

        // Update the cancelled interviews to have CANCELLED status
        const updatedScheduledInterviews = applicant.scheduledInterviews.map(interview =>
            data.interviewIds.includes(interview.externalId)
                ? { ...interview, status: InterviewInvitationStatusEnum.CANCELLED }
                : interview
        );

        return {
            ...applicant,
            scheduledInterviews: updatedScheduledInterviews
        };
    });

    // Check if any applicants were actually updated
    const hasChanges = updatedApplicantsList.some((applicant, index) => {
        const originalApplicant = applicantsList[index];
        if (!originalApplicant || originalApplicant.externalId !== applicant.externalId) {
            return false;
        }
        const originalInterviews = originalApplicant.scheduledInterviews || [];
        const updatedInterviews = applicant.scheduledInterviews || [];

        // Check if any interview status changed to CANCELLED by comparing by externalId
        return originalInterviews.some(originalInterview => {
            const updatedInterview = updatedInterviews.find(
                interview => interview.externalId === originalInterview.externalId
            );
            return updatedInterview &&
                originalInterview.status !== updatedInterview.status &&
                updatedInterview.status === InterviewInvitationStatusEnum.CANCELLED;
        });
    });

    if (hasChanges) {
        dispatch(forceSetApplicantsList(updatedApplicantsList));
        dispatch(updateApplicantScheduledInterviewsInKanbanView(updatedApplicantsList));

        // Update selectedApplicant if it exists and has cancelled interviews
        if (selectedApplicant?.externalId) {
            const updatedSelectedApplicant = updatedApplicantsList.find(
                applicant => applicant.externalId === selectedApplicant.externalId
            );

            if (updatedSelectedApplicant) {
                // Check if selectedApplicant's interviews were updated by comparing by externalId
                const originalInterviews = selectedApplicant.scheduledInterviews || [];
                const updatedInterviews = updatedSelectedApplicant.scheduledInterviews || [];

                const hasSelectedApplicantChanges = originalInterviews.some(originalInterview => {
                    const updatedInterview = updatedInterviews.find(
                        interview => interview.externalId === originalInterview.externalId
                    );
                    return updatedInterview &&
                        originalInterview.status !== updatedInterview.status &&
                        updatedInterview.status === InterviewInvitationStatusEnum.CANCELLED;
                });

                if (hasSelectedApplicantChanges) {
                    dispatch(updateSelectedApplicant(updatedSelectedApplicant));
                }
            }
        }
    }

    return responseData;
});

/**
 * Updates the applicant's scheduledInterviews in the kanban column where the applicant exists.
 * Only runs when the selected view is card view (kanban). Call after sending or canceling an interview invitation.
 */
export function updateApplicantScheduledInterviewsInKanbanView(applicants: ApplicantListItem[]) {
    return (dispatch: ThunkDispatch<RootState, void, AnyAction>, getState: () => RootState) => {
        const state = getState();
        if (state.massHiring?.applicantsView === ApplicantsViewEnum.CARD && applicants?.length > 0) {
            dispatch(updateApplicantsScheduledInterviewsInKanbanColumns(applicants));
        }
    };
}

export const acceptInterviewInvitation = createAsyncThunk(`${appKey}/acceptInterviewInvitation`, async (data: { interviewIds: string[], result?: InterviewInvitationResultEnum }) => {
    const response = await axios.post(`${apiBaseUrl}/accept`, { invitationExternalIds: data.interviewIds, ...(data.result && { result: data.result }) });
    return response.data?.data;
});

export const markAsNotAttendedInterviewInvitation = createAsyncThunk(`${appKey}/markAsNotAttendedInterviewInvitation`, async (data: { interviewIds: string[] }) => {
    const response = await axios.post(`${apiBaseUrl}/mark-not-attended`, { invitationExternalIds: data.interviewIds },);
    return response.data?.data;
});

export const getInterviewMessageTemplate = createAsyncThunk(`${appKey}/getInterviewMessageTemplate`, async (systemTemplateType: InterviewMessageTemplateTypeEnum, { getState }): Promise<WhatsAppTemplate | null> => {

    const state = getState() as RootState;
    const cachedTemplate = state.interviewScheduling.messageTemplatesCache.find((cached) => cached && cached.systemTemplateType === systemTemplateType);

    if (cachedTemplate) {
        return cachedTemplate;
    }

    const response = await axios.get(`${apiWhatsappTemplatesUrl}`, {
        params: {
            systemTemplateType,
            isSystemTemplate: true,
            limit: 1,
            page: 1
        }
    });
    const templates: WhatsAppTemplate[] = response.data?.data?.templates || [];
    return templates.length > 0 ? templates[0] : null;
});


export const setInterviewsSchedule = createAction<InterviewSchedulingInterface[]>(`${appKey}/setInterviewsSchedule`);
export const setActiveInterviewScheduleTab = createAction<InterviewScheduleTabEnum>(`${appKey}/setActiveInterviewScheduleTab`);
export const setInterviewsScheduleFilters = createAction<Partial<InterviewSchedulingFilters>>(`${appKey}/setInterviewsScheduleFilters`);
export const forceSetInterviewsScheduleFilters = createAction<Partial<InterviewSchedulingFilters>>(`${appKey}/forceSetInterviewsScheduleFilters`);
export const setSelectedInterviewIds = createAction<string[]>(`${appKey}/setSelectedInterviewIds`);
export const setSelectedInterview = createAction<InterviewSchedulingInterface | null>(`${appKey}/setSelectedInterview`);
export const setJobIdForApplicantInterview = createAction<string | null>(`${appKey}/setJobIdForApplicantInterview`);
export const clearMessageTemplatesCache = createAction(`${appKey}/clearMessageTemplatesCache`);
export const defaultInterviewScheduleFilters: InterviewSchedulingFilters = {
    tab: InterviewScheduleTabEnum.UPCOMING,
    dateFrom: undefined,
    dateTo: undefined,
    skip: 0,
    page: 1,
    limit: INTERVIEW_INVITATIONS_PAGE_SIZE,
};

const initialState: InterviewSchedulingInitialState = {
    isLoadingInterviewsSchedule: false,
    activeInterviewScheduleTab: InterviewScheduleTabEnum.UPCOMING,
    interviewsSchedule: [],
    interviewsScheduleFilters: defaultInterviewScheduleFilters,
    interviewsScheduleCount: 0,
    upcomingInterviewsScheduleCount: 0,
    selectedInterviewIds: [],
    selectedInterview: null,
    isLoadingAppointments: false,
    appointments: [],
    isLoadingLocations: false,
    locations: [],
    // Side menu states
    isSidebarVisible: false,
    sideMenuAction: null,
    // Modal states
    showCancelInterviewModal: false,
    cancelInterviewData: undefined,
    showRejectInterviewModal: false,
    rejectInterviewData: undefined,
    showDeleteSlotModal: false,
    deleteSlotData: undefined,
    selectedAppointment: null,
    showConfirmLocationChangeModal: false,
    locationChangeConfirmed: false,
    showCancelAppointmentModal: false,
    cancelAppointmentData: undefined,
    showConfirmScheduleInterviewModal: false,
    confirmScheduleInterviewData: undefined,
    showConfirmCancelOnboardingModal: false,
    confirmCancelOnboardingData: undefined,
    selectedApplicantsForInterviewIds: [],
    isSavingAppointment: false,
    isSendingInvitation: false,
    isModifyingLocation: false,
    isModifyingSlots: false,
    isCancelingAppointment: false,
    isRejectingInterview: false,
    isCancelingInterview: false,
    isAcceptingInterview: false,
    isMarkingAsNotAttended: false,
    isLoadingFilters: false,
    filterOptions: null,
    jobIdForApplicantInterview: null,
    messageTemplatesCache: [],
}

export const appInterviewSchedulingSlice = createSlice({
    name: appKey,
    initialState,
    reducers: {
        setShowCancelInterviewModal: (state, action) => {
            state.showCancelInterviewModal = action.payload;
        },
        setCancelInterviewData: (state, action) => {
            state.cancelInterviewData = action.payload;
        },
        setShowRejectInterviewModal: (state, action) => {
            state.showRejectInterviewModal = action.payload;
        },
        setRejectInterviewData: (state, action) => {
            state.rejectInterviewData = action.payload;
        },
        setShowDeleteSlotModal: (state, action) => {
            state.showDeleteSlotModal = action.payload;
        },
        setDeleteSlotData: (state, action) => {
            state.deleteSlotData = action.payload;
        },
        setJobIdForApplicantInterview: (state, action) => {
            state.jobIdForApplicantInterview = action.payload;
        },
        setShowConfirmLocationChangeModal: (state, action) => {
            state.showConfirmLocationChangeModal = action.payload;
            // Reset confirmation flag when modal is closed
            if (!action.payload) {
                state.locationChangeConfirmed = false;
            }
        },
        setLocationChangeConfirmed: (state, action) => {
            state.locationChangeConfirmed = action.payload;
        },
        setShowCancelAppointmentModal: (state, action) => {
            state.showCancelAppointmentModal = action.payload;
        },
        setCancelAppointmentData: (state, action) => {
            state.cancelAppointmentData = action.payload;
        },
        setShowConfirmScheduleInterviewModal: (state, action) => {
            state.showConfirmScheduleInterviewModal = action.payload;
        },
        setConfirmScheduleInterviewData: (state, action) => {
            state.confirmScheduleInterviewData = action.payload;
        },
        setShowConfirmCancelOnboardingModal: (state, action) => {
            state.showConfirmCancelOnboardingModal = action.payload;
        },
        setConfirmCancelOnboardingData: (state, action) => {
            state.confirmCancelOnboardingData = action.payload;
        },
        setIsSidebarVisible: (state, action) => {
            state.isSidebarVisible = action.payload;
        },
        setInterviewSchedulingSideMenuAction: (state, action) => {
            state.sideMenuAction = action.payload;
        },
        setSelectedAppointment: (state, action) => {
            state.selectedAppointment = action.payload;
        },
        setSelectedApplicantsForInterviewIds: (state, action) => {
            state.selectedApplicantsForInterviewIds = action.payload;
        },
        setIsSavingAppointment: (state, action) => {
            state.isSavingAppointment = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(getInterviewsSchedule.pending, (state, action) => {
                state.isLoadingInterviewsSchedule = true;
            })
            .addCase(getInterviewsSchedule.fulfilled, (state, action) => {
                state.interviewsSchedule = action.payload.invitations || [];
                state.interviewsScheduleCount = action.payload.total || 0;
                // Store upcoming count separately when fetching upcoming interviews
                const requestedTab = action.meta.arg?.tab;
                if (requestedTab === InterviewScheduleTabEnum.UPCOMING) {
                    state.upcomingInterviewsScheduleCount = action.payload.total || 0;
                }
                state.isLoadingInterviewsSchedule = false;
            })
            .addCase(getInterviewsSchedule.rejected, (state, action) => {
                state.isLoadingInterviewsSchedule = false;
                Toast.Error({ content: action.error?.message });
            })
            .addCase(setActiveInterviewScheduleTab, (state, action) => {
                state.activeInterviewScheduleTab = action.payload;
            })
            .addCase(setInterviewsScheduleFilters, (state, action) => {
                if (action.payload && typeof action.payload === 'object') {
                    state.interviewsScheduleFilters = { ...state.interviewsScheduleFilters, ...action.payload };
                }
            })
            .addCase(forceSetInterviewsScheduleFilters, (state, action) => {
                state.interviewsScheduleFilters = action.payload;
            })
            .addCase(setSelectedInterviewIds, (state, action) => {
                state.selectedInterviewIds = action.payload;
            })
            .addCase(setSelectedInterview, (state, action) => {
                state.selectedInterview = action.payload;
            })
            .addCase(getInterviewAppointments.pending, (state) => {
                state.isLoadingAppointments = true;
            })
            .addCase(getInterviewAppointments.fulfilled, (state, action) => {
                state.appointments = action.payload.appointments || [];
                state.isLoadingAppointments = false;
            })
            .addCase(getInterviewAppointments.rejected, (state, action) => {
                state.isLoadingAppointments = false;
                Toast.Error({ content: action.error?.message || 'Failed to load appointments' });
            })
            .addCase(getInterviewLocations.pending, (state) => {
                state.isLoadingLocations = true;
            })
            .addCase(getInterviewLocations.fulfilled, (state, action) => {
                state.locations = action.payload.locations || [];
                state.isLoadingLocations = false;
            })
            .addCase(getInterviewLocations.rejected, (state, action) => {
                state.isLoadingLocations = false;
                Toast.Error({ content: action.error?.message || 'Failed to load locations' });
            })
            .addCase(getFilters.pending, (state) => {
                state.isLoadingFilters = true;
            })
            .addCase(getFilters.fulfilled, (state, action) => {
                state.filterOptions = action.payload;
                state.isLoadingFilters = false;
            })
            .addCase(getFilters.rejected, (state, action) => {
                state.isLoadingFilters = false;
                Toast.Error({ content: action.error?.message || 'Failed to load filter options' });
            })
            .addCase(bulkUpdateInterviewResults.pending, (state) => {
                state.isLoadingInterviewsSchedule = true;
            })
            .addCase(bulkUpdateInterviewResults.fulfilled, (state, action) => {
                state.isLoadingInterviewsSchedule = false;
                const { interviewIds, result } = action.meta.arg;

                // Update the result/status for selected interviews based on result enum
                state.interviewsSchedule = state.interviewsSchedule.map(interview => {
                    if (!interviewIds.includes(interview.externalId)) {
                        return interview;
                    }

                    switch (result) {
                        case InterviewInvitationResultEnum.REJECTED:
                            return { ...interview, result: InterviewInvitationResultEnum.REJECTED, status: 'REJECTED' as any };
                        case InterviewInvitationResultEnum.PASSED:
                            return { ...interview, result: InterviewInvitationResultEnum.PASSED };
                        case InterviewInvitationResultEnum.PASSED_AND_ONBOARDING:
                            return { ...interview, result: InterviewInvitationResultEnum.PASSED_AND_ONBOARDING };
                        case InterviewInvitationResultEnum.NOT_ATTENDED:
                            return { ...interview, result: InterviewInvitationResultEnum.NOT_ATTENDED };
                        default:
                            return { ...interview, result };
                    }
                });

                // Toast.Success({ content: t('interview_results_updated_successfully') });
                // Clear selected interview IDs after update
                state.selectedInterviewIds = [];
            })
            .addCase(bulkUpdateInterviewResults.rejected, (state, action) => {
                state.isLoadingInterviewsSchedule = false;
                Toast.Error({ content: action.error?.message || 'Failed to update interview results' });
            })
            .addCase(saveInterviewAppointment.pending, (state) => {
                state.isSavingAppointment = true;
            })
            .addCase(saveInterviewAppointment.fulfilled, (state, action) => {
                state.isSavingAppointment = false;
                Toast.Success({ content: t('interview_appointment_saved_successfully') });
                // Optionally refresh appointments list
                // dispatchThunk(getInterviewAppointments());
            })
            .addCase(saveInterviewAppointment.rejected, (state, action) => {
                state.isSavingAppointment = false;
                Toast.Error({ content: action.error?.message || 'Failed to save interview appointment' });
            })
            .addCase(sendInterviewInvitation.pending, (state) => {
                state.isSendingInvitation = true;
            })
            .addCase(sendInterviewInvitation.fulfilled, (state, action) => {
                state.isSendingInvitation = false;
                Toast.Success({ content: t('interview_invitation_sent_successfully') });
                // Optionally refresh appointments list
                // dispatchThunk(getInterviewAppointments());
            })
            .addCase(sendInterviewInvitation.rejected, (state, action) => {
                state.isSendingInvitation = false;
                Toast.Error({ content: action.error?.message || 'Failed to send interview invitation' });
            })
            .addCase(modifyInterviewAppointmentLocation.pending, (state) => {
                state.isModifyingLocation = true;
            })
            .addCase(modifyInterviewAppointmentLocation.fulfilled, (state, action) => {
                state.isModifyingLocation = false;
                Toast.Success({ content: t('interview_location_updated_successfully') });
            })
            .addCase(modifyInterviewAppointmentLocation.rejected, (state, action) => {
                state.isModifyingLocation = false;
                Toast.Error({ content: action.error?.message || 'Failed to update interview location' });
            })
            .addCase(modifyInterviewAppointmentSlots.pending, (state) => {
                state.isModifyingSlots = true;
            })
            .addCase(modifyInterviewAppointmentSlots.fulfilled, (state, action) => {
                state.isModifyingSlots = false;
                Toast.Success({ content: t('interview_slots_updated_successfully') });
            })
            .addCase(modifyInterviewAppointmentSlots.rejected, (state, action) => {
                state.isModifyingSlots = false;
                Toast.Error({ content: action.error?.message || 'Failed to update interview slots' });
            })
            .addCase(cancelInterviewAppointment.pending, (state) => {
                state.isCancelingAppointment = true;
            })
            .addCase(cancelInterviewAppointment.fulfilled, (state, action) => {
                state.isCancelingAppointment = false;
                Toast.Success({ content: t('interview_appointment_cancelled_successfully') });
            })
            .addCase(cancelInterviewAppointment.rejected, (state, action) => {
                state.isCancelingAppointment = false;
                Toast.Error({ content: action.error?.message || 'Failed to cancel interview appointment' });
            })
            .addCase(rejectInterviewInvitation.pending, (state) => {
                state.isRejectingInterview = true;
            })
            .addCase(rejectInterviewInvitation.fulfilled, (state, action) => {
                state.isRejectingInterview = false;
                const { interviewIds } = action.meta.arg;
                // Update the status and result for rejected interviews
                state.interviewsSchedule = state.interviewsSchedule.map(interview =>
                    interviewIds.includes(interview.externalId)
                        ? { ...interview, status: 'REJECTED' as any, result: 'REJECTED' as any }
                        : interview
                );
                Toast.Success({ content: t('interview_rejected_successfully') });
            })
            .addCase(rejectInterviewInvitation.rejected, (state, action) => {
                state.isRejectingInterview = false;
                Toast.Error({ content: action.error?.message || 'Failed to reject interview' });
            })
            .addCase(cancelInterviewInvitation.pending, (state) => {
                state.isCancelingInterview = true;
            })
            .addCase(cancelInterviewInvitation.fulfilled, (state, action) => {
                state.isCancelingInterview = false;
                const { interviewIds } = action.meta.arg;
                // Update the status for canceled interviews
                state.interviewsSchedule = state.interviewsSchedule.map(interview =>
                    interviewIds.includes(interview.externalId)
                        ? { ...interview, status: 'CANCELLED' as any }
                        : interview
                );
                Toast.Success({ content: t('interview_cancelled_successfully') });
            })
            .addCase(cancelInterviewInvitation.rejected, (state, action) => {
                state.isCancelingInterview = false;
                Toast.Error({ content: action.error?.message || 'Failed to cancel interview' });
            })
            .addCase(acceptInterviewInvitation.pending, (state) => {
                state.isAcceptingInterview = true;
            })
            .addCase(acceptInterviewInvitation.fulfilled, (state, action) => {
                state.isAcceptingInterview = false;
                const { interviewIds, result } = action.meta.arg;
                const resolvedResult = result || InterviewInvitationResultEnum.PASSED;
                // Update the result for accepted interviews
                state.interviewsSchedule = state.interviewsSchedule.map(interview =>
                    interviewIds.includes(interview.externalId)
                        ? { ...interview, result: resolvedResult }
                        : interview
                );
                Toast.Success({ content: t('interview_accepted_successfully') });
            })
            .addCase(acceptInterviewInvitation.rejected, (state, action) => {
                state.isAcceptingInterview = false;
                Toast.Error({ content: action.error?.message || 'Failed to accept interview' });
            })
            .addCase(markAsNotAttendedInterviewInvitation.pending, (state) => {
                state.isMarkingAsNotAttended = true;
            })
            .addCase(markAsNotAttendedInterviewInvitation.fulfilled, (state, action) => {
                state.isMarkingAsNotAttended = false;
                const { interviewIds } = action.meta.arg;
                // Update the result for not attended interviews
                state.interviewsSchedule = state.interviewsSchedule.map(interview =>
                    interviewIds.includes(interview.externalId)
                        ? { ...interview, result: InterviewInvitationResultEnum.NOT_ATTENDED }
                        : interview
                );
                Toast.Success({ content: t('interview_marked_as_not_attended_successfully') });
            })
            .addCase(markAsNotAttendedInterviewInvitation.rejected, (state, action) => {
                state.isMarkingAsNotAttended = false;
                Toast.Error({ content: action.error?.message || 'Failed to mark interview as not attended' });
            })
            .addCase(getInterviewMessageTemplate.fulfilled, (state, action) => {
                // Only cache non-null templates
                if (action.payload) {
                    state.messageTemplatesCache.push(action.payload);
                }
            })
            .addCase(getInterviewMessageTemplate.rejected, (state, action) => {
                state.messageTemplatesCache = [];
                Toast.Error({ content: action.error?.message || 'Failed to get interview message template' });
            })
            .addCase(clearMessageTemplatesCache, (state) => {
                state.messageTemplatesCache = [];
            })

    }
})

export const {
    setShowCancelInterviewModal,
    setCancelInterviewData,
    setShowRejectInterviewModal,
    setRejectInterviewData,
    setShowDeleteSlotModal,
    setDeleteSlotData,
    setShowConfirmLocationChangeModal,
    setLocationChangeConfirmed,
    setShowCancelAppointmentModal,
    setCancelAppointmentData,
    setShowConfirmScheduleInterviewModal,
    setConfirmScheduleInterviewData,
    setShowConfirmCancelOnboardingModal,
    setConfirmCancelOnboardingData,
    setIsSidebarVisible,
    setInterviewSchedulingSideMenuAction,
    setSelectedAppointment,
    setIsSavingAppointment,
} = appInterviewSchedulingSlice.actions;

export default appInterviewSchedulingSlice.reducer
